/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StoplightContext } from '../contexts';
import { Comment, Filter, Stoplight } from '../../config/interfaces';

interface IGetStoplightsByLocationPayload {
  location: string;
  filterData: {
    category?: string;
    createdBy?: string;
    facilityId?: string;
    hasIssues?: boolean;
    hasComment?: boolean;
    locationIds: string[];
    locationType: string;
    origin: string;
    query: string;
    startDate: string;
    endDate?: string;
    subCategory?: string;
    status?: string[];
    sortBy: string;
    sortOrder: string;
    page: number;
  };
}

interface ISearchStoplightsPayload {
  facilityId: string;
  query: string;
  origin: string;
  page: number;
}

/**
 * Redux slice for managing stoplight-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to stoplight rounds, including:
 * - Fetching, creating, updating, and deleting stoplights
 * - Posting and managing comments on stoplights
 * - Fetching stoplights by location, searching, sorting, and pagination
 * - Managing stoplight sharing queue and sharing actions
 * - Setting and clearing filters for stoplights
 * - Managing loading states for all asynchronous stoplight operations
 *
 * @property {object} initialState - The initial state for the stoplight context.
 * @property {Function} getStoplightById - Initiates fetching a stoplight by ID and sets loading state.
 * @property {Function} getStoplightByIdSuccess - Handles successful fetch of a stoplight by ID.
 * @property {Function} getStoplightByIdFailure - Handles failed fetch of a stoplight by ID.
 * @property {Function} postNewStoplight - Initiates posting a new stoplight and sets loading state.
 * @property {Function} postNewStoplightSuccess - Handles successful posting of a new stoplight.
 * @property {Function} postNewStoplightFailure - Handles failed posting of a new stoplight.
 * @property {Function} updateStoplight - Initiates updating a stoplight and sets loading state.
 * @property {Function} updateStoplightSuccess - Handles successful update of a stoplight.
 * @property {Function} updateStoplightFailure - Handles failed update of a stoplight.
 * @property {Function} deleteStoplight - Initiates deleting a stoplight and sets loading state.
 * @property {Function} deleteStoplightSuccess - Handles successful deletion of a stoplight.
 * @property {Function} deleteStoplightFailure - Handles failed deletion of a stoplight.
 * @property {Function} postCommentToStoplight - Initiates posting a comment to a stoplight.
 * @property {Function} postCommentToStoplightSuccess - Handles successful posting of a comment.
 * @property {Function} postCommentToStoplightFailure - Handles failed posting of a comment.
 * @property {Function} clearStoplight - Clears the current stoplight from the state.
 * @property {Function} getStoplightsByLocation - Initiates fetching stoplights by location and sets loading state.
 * @property {Function} getStoplightsByLocationSuccess - Handles successful fetch of stoplights by location.
 * @property {Function} getStoplightsByLocationMetricsSuccess - Handles successful fetch of stoplight metrics by location.
 * @property {Function} getStoplightsByLocationFailure - Handles failed fetch of stoplights by location.
 * @property {Function} setStoplightSort - Sets the sort key and direction for stoplights.
 * @property {Function} setCurrentStoplightPage - Sets the current page for stoplight pagination.
 * @property {Function} setStoplightSearchQuery - Sets the search query for stoplights and resets pagination.
 * @property {Function} getStoplightsByLocationDownload - Initiates download of stoplights by location.
 * @property {Function} searchStoplights - Initiates searching stoplights and sets loading state.
 * @property {Function} searchStoplightsSuccess - Handles successful search of stoplights.
 * @property {Function} searchStoplightsFailure - Handles failed search of stoplights.
 * @property {Function} clearSearchStoplights - Clears searched stoplights from the state.
 * @property {Function} addStoplightToSharingQ - Adds a stoplight to the sharing queue.
 * @property {Function} shareStoplight - Initiates sharing a stoplight and sets loading state.
 * @property {Function} shareStoplightSuccess - Handles successful sharing of a stoplight.
 * @property {Function} shareStoplightFailure - Handles failed sharing of a stoplight.
 * @property {Function} setStoplightFilter - Sets the filter for stoplights and resets pagination.
 * @property {Function} clearStoplightFilter - Clears the stoplight filter and resets pagination.
 *
 * @example
 * dispatch(getStoplightById('stoplight-uuid'));
 * dispatch(postNewStoplight(stoplightData));
 * dispatch(postCommentToStoplight({ id: 'stoplight-uuid', comment: { text: 'Comment' } }));
 * dispatch(getStoplightsByLocation({ facilityId: '12345', locationIds: ['A.3N'], ... }));
 * dispatch(setStoplightSort({ sortKey: 'created', sortDir: 'desc' }));
 * dispatch(shareStoplight({ userName: 'abc12345', email: ['jane@example.com'] }));
 * dispatch(setStoplightFilter({ status: ['completed'] }));
 *
 * @returns {object} The stoplight slice reducer and actions.
 */
const stoplightSlice = createSlice({
  name: 'stoplight',
  initialState: StoplightContext,
  reducers: {
    getStoplightById: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        loadingStoplight: true
      };
    },
    getStoplightByIdSuccess: (state, action: PayloadAction<Stoplight>) => {
      return {
        ...state,
        loadingStoplight: false,
        stoplight: action.payload
      };
    },
    getStoplightByIdFailure: state => {
      return {
        ...state,
        loadingStoplight: false
      };
    },
    postNewStoplight: (state, action: PayloadAction<Stoplight>) => {
      return {
        ...state,
        loadingStoplight: true
      };
    },
    postNewStoplightSuccess: (state, action: PayloadAction<Stoplight>) => {
      return {
        ...state,
        loadingStoplight: false
      };
    },
    postNewStoplightFailure: state => {
      return {
        ...state,
        loadingStoplight: true
      };
    },
    updateStoplight: (state, action: PayloadAction<Stoplight>) => {
      return {
        ...state,
        loadingStoplight: true
      };
    },
    updateStoplightSuccess: state => {
      return {
        ...state,
        loadingStoplight: false
      };
    },
    updateStoplightFailure: state => {
      return {
        ...state,
        loadingStoplight: false
      };
    },
    deleteStoplight: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        loadingStoplight: true
      };
    },
    deleteStoplightSuccess: state => {
      return {
        ...state,
        loadingStoplight: false
      };
    },
    deleteStoplightFailure: state => {
      return {
        ...state,
        loadingStoplight: false
      };
    },
    postCommentToStoplight: (
      state,
      action: PayloadAction<{
        id: string;
        comment: Comment;
      }>
    ) => {
      return { ...state, loadingComment: true };
    },
    postCommentToStoplightSuccess: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        comment: action.payload,
        loadingComment: false
      };
    },
    postCommentToStoplightFailure: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        loadingComment: false
      };
    },
    clearStoplight: state => {
      return {
        ...state,
        stoplight: null
      };
    },
    getStoplightsByLocation: (
      state,
      action: PayloadAction<IGetStoplightsByLocationPayload>
    ) => {
      return {
        ...state,
        loading: true
      };
    },
    getStoplightsByLocationSuccess: (
      state,
      action: PayloadAction<{ results: Stoplight[]; totalCount: number }>
    ) => {
      return {
        ...state,
        loading: false,
        locationStoplights: action.payload.results,
        totalCount: action.payload.totalCount
      };
    },
    getStoplightsByLocationMetricsSuccess: (
      state,
      action: PayloadAction<{
        totalRecordsCount: number;
        completedRecordsCount: number;
        openedRecordsCount: number;
        deferredRecordsCount: number;
      }>
    ) => {
      return {
        ...state,
        loading: false,
        stoplightMetrics: {
          totalRecordsCount: action.payload.totalRecordsCount,
          completedRecordsCount: action.payload.completedRecordsCount,
          openedRecordsCount: action.payload.openedRecordsCount,
          deferredRecordsCount: action.payload.deferredRecordsCount
        }
      };
    },
    getStoplightsByLocationFailure: state => {
      return {
        ...state,
        loading: false,
        locationStoplights: []
      };
    },
    setStoplightSort: (
      state,
      action: PayloadAction<{
        sortKey: string;
        sortDir: string;
      }>
    ) => {
      return {
        ...state,
        ...action.payload
      };
    },
    setCurrentStoplightPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentStoplightPage: action.payload
      };
    },

    setStoplightSearchQuery: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        stoplightSearchQuery: action.payload,
        currentStoplightPage: 1
      };
    },
    getStoplightsByLocationDownload: (state, action: PayloadAction<any>) => {
      return { ...state };
    },
    searchStoplights: (
      state,
      action: PayloadAction<ISearchStoplightsPayload>
    ) => {
      return {
        ...state,
        loading: true,
        searchedStoplights: []
      };
    },
    searchStoplightsSuccess: (state, action: PayloadAction<Stoplight[]>) => {
      return {
        ...state,
        loading: false,
        searchedStoplights: action.payload
      };
    },
    searchStoplightsFailure: state => {
      return {
        ...state,
        loading: false
      };
    },
    clearSearchStoplights: state => {
      return {
        ...state,
        searchedStoplights: []
      };
    },
    addStoplightToSharingQ: (
      state,
      action: PayloadAction<{
        userName: string;
        email: string[];
      }>
    ) => {
      return {
        ...state,
        stoplightSharingQ: action.payload
      };
    },
    shareStoplight: (
      state,
      action: PayloadAction<{
        userName: string;
        email: string[];
      }>
    ) => {
      return {
        ...state,
        loadingStoplightShare: true
      };
    },
    shareStoplightSuccess: state => {
      return {
        ...state,
        loadingStoplightShare: false,
        stoplightSharingQ: {}
      };
    },
    shareStoplightFailure: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        error: action.payload,
        loadingStoplightShare: false
      };
    },
    setStoplightFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        stoplightFilter: action.payload,
        currentStoplightPage: 1
      };
    },
    clearStoplightFilter: state => {
      return {
        ...state,
        stoplightFilter: {} as Filter,
        currentStoplightPage: 1
      };
    }
  }
});

export const {
  getStoplightById,
  getStoplightByIdSuccess,
  getStoplightByIdFailure,
  postNewStoplight,
  postNewStoplightSuccess,
  postNewStoplightFailure,
  updateStoplight,
  updateStoplightSuccess,
  updateStoplightFailure,
  deleteStoplight,
  deleteStoplightSuccess,
  deleteStoplightFailure,
  postCommentToStoplight,
  postCommentToStoplightSuccess,
  postCommentToStoplightFailure,
  clearStoplight,
  getStoplightsByLocation,
  getStoplightsByLocationSuccess,
  getStoplightsByLocationMetricsSuccess,
  getStoplightsByLocationFailure,
  setStoplightSort,
  setCurrentStoplightPage,
  setStoplightSearchQuery,
  getStoplightsByLocationDownload,
  searchStoplights,
  searchStoplightsSuccess,
  searchStoplightsFailure,
  clearSearchStoplights,
  addStoplightToSharingQ,
  shareStoplight,
  shareStoplightSuccess,
  shareStoplightFailure,
  setStoplightFilter,
  clearStoplightFilter
} = stoplightSlice.actions;

export default stoplightSlice.reducer;
